/* URS-F273: Center info admin panel.
 *
 * Lets an admin edit the contact email, phone, legal name and other
 * fields that appear on outbound volunteer emails and in the .ics
 * calendar files. Replaces the previous hardcoded literals in
 * `agenda_email_routes.py` and `core/ics_service.py`.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'legal_name', es: 'Razon social (footer)', en: 'Legal name (footer)', help_es: 'Aparece al pie del email. Ej: "Goya Innova S.L."', help_en: 'Appears at the email footer. E.g. "Goya Innova S.L."' },
  { key: 'contact_email', es: 'Email de contacto para voluntarios', en: 'Volunteer contact email', help_es: 'Email visible al voluntario para reprogramar citas.', help_en: 'Email shown to volunteers to reschedule appointments.' },
  { key: 'contact_phone', es: 'Telefono de contacto', en: 'Contact phone', help_es: 'Telefono visible al voluntario en el email.', help_en: 'Phone shown to volunteers in the email.' },
  { key: 'city', es: 'Ciudad', en: 'City', help_es: 'Aparece junto a la razon social. Ej: "Madrid, Espana"', help_en: 'Appears next to legal name.' },
  { key: 'location_default', es: 'Ubicacion por defecto (calendario)', en: 'Default location (calendar)', help_es: 'Usada en el .ics cuando el estudio no tiene su propia location.', help_en: 'Used in .ics when the study has no own location.' },
  { key: 'ics_organizer_email', es: 'Email organizador (.ics)', en: 'Organizer email (.ics)', help_es: 'Campo ORGANIZER del archivo .ics. Suele coincidir con el email de contacto.', help_en: 'ORGANIZER field of the .ics file.' },
  { key: 'ics_organizer_name', es: 'Nombre organizador (.ics)', en: 'Organizer name (.ics)', help_es: 'Etiqueta del ORGANIZER en el .ics.', help_en: 'ORGANIZER label in the .ics.' },
  { key: 'ics_prodid', es: 'PRODID (.ics)', en: 'PRODID (.ics)', help_es: 'Identificador tecnico de la app productora del .ics (RFC 5545).', help_en: 'Technical product id of the .ics generator (RFC 5545).' },
];

const BRAND_FIELDS = [
  { key: 'brand_primary_color', es: 'Color principal', en: 'Primary color', help_es: 'Hex (#RRGGBB). Se aplica al fondo de cabecera, bordes y enlaces del email.', help_en: 'Hex (#RRGGBB). Used for header background, borders and links.', is_color: true },
  { key: 'brand_logo_url', es: 'URL del logo (cabecera email)', en: 'Logo URL (email header)', help_es: 'URL publica de la imagen del logo (PNG/JPG con fondo transparente recomendado). Si esta vacio, se muestra solo el texto.', help_en: 'Public image URL (transparent PNG/JPG recommended). Empty = text-only header.' },
  { key: 'brand_email_header_text', es: 'Texto cabecera email', en: 'Email header text', help_es: 'Titulo grande de la cabecera del email.', help_en: 'Big title rendered in the email header.' },
  { key: 'brand_email_subheader_text', es: 'Subtitulo cabecera email', en: 'Email subheader text', help_es: 'Texto pequeno bajo el titulo. Ej: "Confirmacion de citas".', help_en: 'Small text below the title.' },
  { key: 'email_subject_appointments', es: 'Asunto del email de citas', en: 'Appointment email subject', help_es: 'Asunto exacto que vera el voluntario en su bandeja de entrada.', help_en: 'Exact subject the volunteer will see in their inbox.' },
];

export default function CenterInfoPanel({ lang = 'es' }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/config/center-info`, { withCredentials: true })
      .then(r => setForm(r.data))
      .catch(() => toast.error(lang === 'es' ? 'Error cargando datos del centro' : 'Error loading center info'));
  }, [lang]);

  const onChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const allKeys = [...FIELDS, ...BRAND_FIELDS].map(f => f.key);
      const payload = allKeys.reduce((acc, k) => ({ ...acc, [k]: form[k] || '' }), {});
      const { data } = await axios.put(`${API_URL}/api/config/center-info`, payload, { withCredentials: true });
      setForm(data);
      toast.success(lang === 'es' ? 'Datos del centro guardados' : 'Center info saved');
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al guardar' : 'Error saving'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-sm text-stone-500 py-8 text-center">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  const primary = form.brand_primary_color || '#1f3e80';
  const headerTxt = form.brand_email_header_text || 'Investigaciones Clinicas';
  const subTxt = form.brand_email_subheader_text || 'eCRD - Confirmacion de citas';
  const logoUrl = (form.brand_logo_url || '').trim();

  const renderField = (f) => (
    <div key={f.key} className="space-y-1">
      <Label htmlFor={`center-${f.key}`} className="text-xs font-medium text-stone-700">
        {lang === 'es' ? f.es : f.en}
      </Label>
      <div className={f.is_color ? 'flex items-center gap-2' : ''}>
        {f.is_color && (
          <input
            type="color"
            value={form[f.key] || '#1f3e80'}
            onChange={e => onChange(f.key, e.target.value)}
            className="h-9 w-12 rounded border border-stone-200 cursor-pointer shrink-0"
            data-testid={`center-color-${f.key}`}
            aria-label={lang === 'es' ? `Selector color ${f.es}` : `Color picker ${f.en}`}
          />
        )}
        <Input
          id={`center-${f.key}`}
          data-testid={`center-input-${f.key}`}
          value={form[f.key] || ''}
          onChange={e => onChange(f.key, e.target.value)}
          placeholder={lang === 'es' ? f.es : f.en}
        />
      </div>
      <p className="text-[10px] text-stone-400">
        {lang === 'es' ? f.help_es : f.help_en}
      </p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="center-info-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {lang === 'es' ? 'Datos del Centro' : 'Center info'}
          </CardTitle>
          <p className="text-xs text-stone-500 mt-1">
            {lang === 'es'
              ? 'Estos valores aparecen en los emails automaticos a voluntarios y en los archivos .ics de citas. Editalos aqui sin tocar codigo.'
              : 'These values appear in automated emails to volunteers and in appointment .ics files. Edit them here without touching code.'}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(renderField)}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="brand-section">
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {lang === 'es' ? 'Marca del email' : 'Email brand'}
          </CardTitle>
          <p className="text-xs text-stone-500 mt-1">
            {lang === 'es'
              ? 'Personaliza el aspecto visual (color, logo, textos) del email que reciben los voluntarios.'
              : 'Customise the look (color, logo, copy) of volunteer-facing emails.'}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {BRAND_FIELDS.map(renderField)}
          </div>
          {/* Live preview of the email header */}
          <div className="rounded-md border border-stone-200 overflow-hidden" data-testid="brand-preview">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 px-3 py-1.5 bg-stone-50 border-b border-stone-200">
              {lang === 'es' ? 'Vista previa cabecera email' : 'Email header preview'}
            </div>
            <div
              style={{ background: primary, padding: '18px 24px', color: '#fff', fontFamily: 'Arial, sans-serif' }}
            >
              {logoUrl ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src={logoUrl} alt="logo" style={{ height: 48, maxWidth: 64, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 0.5 }}>{headerTxt}</div>
                    <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{subTxt}</div>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 0.5 }}>{headerTxt}</div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{subTxt}</div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          data-testid="center-info-save"
          onClick={onSave}
          disabled={saving}
          className="bg-[#1f3e80] hover:bg-[#162d5e]"
        >
          {saving ? (lang === 'es' ? 'Guardando...' : 'Saving...') : (lang === 'es' ? 'Guardar' : 'Save')}
        </Button>
      </div>
    </div>
  );
}
